import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  MenuItem,
  CircularProgress,
  InputAdornment,
  Divider,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Assignment as AssignIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Print as PrintIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ViewKanban as BoardIcon,
  AttachMoney as MoneyIcon,
  CheckBox as TaskIcon,
  StickyNote2 as StickerIcon,
  Notes as NotesIcon,
  Archive as ArchiveIcon,
  OpenInNew as OpenIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import CompanyFormDialog from '../components/CompanyFormDialog';

const Companies = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' или 'list'
  const [districtFilter, setDistrictFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [managers, setManagers] = useState([]);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [viewCompany, setViewCompany] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [openMassAssignDialog, setOpenMassAssignDialog] = useState(false);
  const [selectedManagerForMass, setSelectedManagerForMass] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  


  useEffect(() => {
    fetchCompanies();
    if (isAdmin) {
      fetchManagers();
    }
  }, [isAdmin]);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/companies');
      setCompanies(response.data);
    } catch (error) {
      console.error('Ошибка загрузки компаний:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await axios.get('/api/users/managers');
      setManagers(response.data);
    } catch (error) {
      console.error('Ошибка загрузки менеджеров:', error);
    }
  };

  const handleOpenDialog = (company = null) => {
    setSelectedCompany(company);
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить эту компанию?')) {
      try {
        await axios.delete(`/api/companies/${id}`);
        fetchCompanies();
      } catch (error) {
        console.error('Ошибка удаления компании:', error);
      }
    }
  };

  const handleOpenAssignDialog = (company) => {
    setSelectedCompany(company);
    setOpenAssignDialog(true);
  };

  const handleAssign = async (managerIds) => {
    try {
      await axios.post(`/api/companies/${selectedCompany._id}/assign`, { managerIds });
      fetchCompanies();
      setOpenAssignDialog(false);
    } catch (error) {
      console.error('Ошибка назначения компании:', error);
    }
  };

  const handlePrint = () => {
    if (!viewCompany) {
      alert('Данные компании не загружены');
      return;
    }
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Карточка компании - ${viewCompany.name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; color: #333; line-height: 1.6; }
          .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #5e81ac; }
          .header h1 { color: #5e81ac; font-size: 28px; margin-bottom: 10px; }
          .header .status { display: inline-block; background: ${viewCompany.color || '#5e81ac'}; color: white; padding: 5px 15px; border-radius: 20px; font-size: 14px; }
          .section { margin-bottom: 25px; page-break-inside: avoid; }
          .section-title { font-size: 18px; font-weight: 600; color: #5e81ac; margin-bottom: 15px; padding-bottom: 8px; border-bottom: 2px solid #e0e0e0; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .info-row { display: flex; padding: 10px; background: #f5f5f5; border-radius: 5px; }
          .info-label { font-weight: 600; color: #666; min-width: 180px; }
          .info-value { color: #333; flex: 1; }
          .full-width { grid-column: 1 / -1; }
          .requisites-table { width: 100%; border-collapse: collapse; }
          .requisites-table td { padding: 10px; border: 1px solid #e0e0e0; }
          .requisites-table td:first-child { background: #f5f5f5; font-weight: 600; width: 35%; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #e0e0e0; text-align: center; color: #888; font-size: 12px; }
          .logo { font-size: 24px; font-weight: bold; color: #5e81ac; margin-bottom: 5px; }
          .managers { display: flex; gap: 10px; flex-wrap: wrap; }
          .manager-badge { background: #e3f2fd; color: #5e81ac; padding: 5px 12px; border-radius: 15px; font-size: 14px; }
          @media print { body { padding: 20px; } .section { margin-bottom: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">Nord CRM</div>
          <h1>${viewCompany.name}</h1>
          <span class="status">${viewCompany.status || 'Активная'}</span>
        </div>
        <div class="section">
          <div class="section-title">📋 Основная информация</div>
          <div class="info-grid">
            <div class="info-row"><span class="info-label">Название компании:</span><span class="info-value">${viewCompany.name || '—'}</span></div>
            <div class="info-row"><span class="info-label">Контактное лицо:</span><span class="info-value">${viewCompany.contactPerson || '—'}</span></div>
            <div class="info-row"><span class="info-label">Телефон:</span><span class="info-value">${viewCompany.phone || '—'}</span></div>
            <div class="info-row"><span class="info-label">Email:</span><span class="info-value">${viewCompany.email || '—'}</span></div>
            <div class="info-row full-width"><span class="info-label">Адрес:</span><span class="info-value">${viewCompany.address || '—'}</span></div>
          </div>
        </div>
        ${viewCompany.requisites && Object.values(viewCompany.requisites).some(val => val) ? `
        <div class="section">
          <div class="section-title">🏦 Реквизиты предприятия</div>
          <table class="requisites-table">
            ${viewCompany.requisites.inn ? `<tr><td>ИНН</td><td>${viewCompany.requisites.inn}</td></tr>` : ''}
            ${viewCompany.requisites.kpp ? `<tr><td>КПП</td><td>${viewCompany.requisites.kpp}</td></tr>` : ''}
            ${viewCompany.requisites.ogrn ? `<tr><td>ОГРН</td><td>${viewCompany.requisites.ogrn}</td></tr>` : ''}
            ${viewCompany.requisites.bankName ? `<tr><td>Наименование банка</td><td>${viewCompany.requisites.bankName}</td></tr>` : ''}
            ${viewCompany.requisites.bik ? `<tr><td>БИК</td><td>${viewCompany.requisites.bik}</td></tr>` : ''}
            ${viewCompany.requisites.checkingAccount ? `<tr><td>Расчётный счёт</td><td>${viewCompany.requisites.checkingAccount}</td></tr>` : ''}
            ${viewCompany.requisites.correspondentAccount ? `<tr><td>Корреспондентский счёт</td><td>${viewCompany.requisites.correspondentAccount}</td></tr>` : ''}
          </table>
        </div>` : ''}
        ${viewCompany.additionalInfo ? `
        <div class="section">
          <div class="section-title">📝 Дополнительная информация</div>
          <div class="info-row full-width"><span class="info-value">${viewCompany.additionalInfo}</span></div>
        </div>` : ''}
        ${viewCompany.assignedTo && viewCompany.assignedTo.length > 0 ? `
        <div class="section">
          <div class="section-title">👥 Назначенные менеджеры</div>
          <div class="managers">${viewCompany.assignedTo.map(m => `<span class="manager-badge">${m.firstName} ${m.lastName}</span>`).join('')}</div>
        </div>` : ''}
        <div class="footer">
          <p>Документ сформирован: ${new Date().toLocaleDateString('ru-RU')} в ${new Date().toLocaleTimeString('ru-RU')}</p>
          <p>Nord CRM © ${new Date().getFullYear()}</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Загрузка истории компании
  const fetchHistory = async (companyId) => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(`/api/companies/${companyId}/history`);
      setHistoryData(response.data);
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = () => {
    if (viewCompany) {
      setHistoryOpen(true);
      fetchHistory(viewCompany._id);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const priorityColors = {
    'низкий': '#a3be8c',
    'средний': '#ebcb8b',
    'высокий': '#d08770',
    'критический': '#bf616a',
  };

  // Получаем уникальные районы из компаний
  const uniqueDistricts = [...new Set(companies.filter(c => c.district).map(c => c.district))].sort();

  const filteredCompanies = companies.filter((company) => {
    const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (company.district && company.district.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesDistrict = !districtFilter || company.district === districtFilter;
    return matchesSearch && matchesDistrict;
  });


  if (loading) {
    return (
      <Layout>
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          minHeight="60vh"
          flexDirection="column"
          gap={2}
        >
          <CircularProgress size={60} thickness={2} />
          <Typography variant="body2" color="text.secondary">
            Загрузка компаний...
          </Typography>
        </Box>
      </Layout>
    );
  }


  return (
    <Layout>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 800,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #e6edf3 0%, #88c0d0 100%)'
              : 'linear-gradient(135deg, #2e3440 0%, #5e81ac 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          База компаний
        </Typography>
        <Box display="flex" gap={2}>
          {isAdmin && selectedCompanies.length > 0 && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<AssignIcon />}
              onClick={() => setOpenMassAssignDialog(true)}
            >
              Назначить ({selectedCompanies.length})
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="large"
          >
            Добавить компанию
          </Button>
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          sx={{ flex: 1, minWidth: 200 }}
          placeholder="Поиск компаний..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        
        <TextField
          select
          label="Район"
          value={districtFilter}
          onChange={(e) => setDistrictFilter(e.target.value)}
          sx={{ minWidth: 180 }}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FilterListIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        >
          <MenuItem value="">Все районы</MenuItem>
          {uniqueDistricts.map((district) => (
            <MenuItem key={district} value={district}>
              {district}
            </MenuItem>
          ))}
        </TextField>

        <Box sx={{ display: 'flex', gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}>
          <Tooltip title="Плитка">
            <IconButton 
              size="small" 
              onClick={() => setViewMode('grid')}
              sx={{ 
                bgcolor: viewMode === 'grid' ? 'primary.main' : 'transparent',
                color: viewMode === 'grid' ? 'white' : 'text.secondary',
                '&:hover': { bgcolor: viewMode === 'grid' ? 'primary.dark' : 'action.hover' },
              }}
            >
              <ViewModuleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Список">
            <IconButton 
              size="small" 
              onClick={() => setViewMode('list')}
              sx={{ 
                bgcolor: viewMode === 'list' ? 'primary.main' : 'transparent',
                color: viewMode === 'list' ? 'white' : 'text.secondary',
                '&:hover': { bgcolor: viewMode === 'list' ? 'primary.dark' : 'action.hover' },
              }}
            >
              <ViewListIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={viewMode === 'list' ? 1 : 3}>
        {filteredCompanies.map((company, index) => (
          <Grid item xs={12} sm={viewMode === 'list' ? 12 : 6} md={viewMode === 'list' ? 12 : 4} key={company._id}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderLeft: `4px solid ${company.color}`,
                position: 'relative',
                opacity: company.approved ? 1 : 0.85,
                animation: `fadeInUp 0.4s ease ${index * 0.05}s both`,
                '@keyframes fadeInUp': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
                background: isDarkMode 
                  ? (company.approved ? 'rgba(22, 27, 34, 0.7)' : 'rgba(235, 203, 139, 0.08)')
                  : (company.approved ? '#ffffff' : '#fffbeb'),
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: isDarkMode 
                    ? `0 12px 40px rgba(0, 0, 0, 0.5), 0 0 20px ${company.color}20`
                    : '0 12px 40px rgba(0, 0, 0, 0.15)',
                },
              }}
              onClick={() => {
                setViewCompany(company);
                setOpenDetailDialog(true);
              }}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography
                    variant="h6"
                    sx={{ 
                      fontWeight: 700, 
                      flex: 1,
                      color: 'text.primary',
                    }}
                  >
                    {company.name}
                  </Typography>
                  <Box onClick={(e) => e.stopPropagation()}>
                    {isAdmin && !company.approved && (
                      <>
                        <IconButton 
                          size="small" 
                          onClick={async () => {
                            try {
                              await axios.post(`/api/companies/${company._id}/approve`);
                              fetchCompanies();
                            } catch (error) {
                              console.error('Ошибка подтверждения:', error);
                              alert('Ошибка подтверждения компании');
                            }
                          }}
                          title="Подтвердить компанию"
                          sx={{ color: '#a3be8c' }}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={async () => {
                            if (window.confirm('Отклонить и удалить эту компанию?')) {
                              try {
                                await axios.post(`/api/companies/${company._id}/reject`);
                                fetchCompanies();
                              } catch (error) {
                                console.error('Ошибка отклонения:', error);
                                alert('Ошибка отклонения компании');
                              }
                            }
                          }}
                          title="Отклонить компанию"
                          sx={{ color: '#bf616a' }}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                    <IconButton size="small" onClick={() => handleOpenDialog(company)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {isAdmin && (
                      <>
                        <IconButton size="small" onClick={() => handleOpenAssignDialog(company)}>
                          <AssignIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(company._id)} sx={{ color: '#bf616a' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={company.status}
                    size="small"
                    sx={{
                      backgroundColor: company.color,
                      color: '#ffffff',
                      fontWeight: 600,
                      boxShadow: `0 2px 8px ${company.color}40`,
                    }}
                  />
                  {!company.approved && (
                    <Chip
                      icon={<HourglassEmptyIcon sx={{ fontSize: 16 }} />}
                      label="Ожидает"
                      size="small"
                      sx={{
                        backgroundColor: isDarkMode ? 'rgba(235, 203, 139, 0.2)' : '#fff3cd',
                        color: '#ebcb8b',
                        fontWeight: 500,
                        border: '1px solid rgba(235, 203, 139, 0.3)',
                      }}
                    />
                  )}
                </Box>

                {company.phone && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    📱 {company.phone}
                  </Typography>
                )}

                {company.contactPerson && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    👤 {company.contactPerson}
                  </Typography>
                )}

                {company.email && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    📧 {company.email}
                  </Typography>
                )}

                {company.address && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    📍 {company.address}
                  </Typography>
                )}

                {company.district && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    🏘️ {company.district}
                  </Typography>
                )}

                {company.assignedTo && company.assignedTo.length > 0 && (
                  <Box 
                    mt={1.5} 
                    pt={1.5} 
                    sx={{ 
                      borderTop: '1px solid',
                      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Назначено:
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                      {company.assignedTo.map(m => `${m.firstName} ${m.lastName}`).join(', ')}
                    </Typography>
                  </Box>
                )}

                {isAdmin && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      zIndex: 10,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedCompanies(prev =>
                          prev.includes(company._id)
                            ? prev.filter(id => id !== company._id)
                            : [...prev, company._id]
                        );
                      }}
                      sx={{
                        bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'white',
                        border: selectedCompanies.includes(company._id) 
                          ? '2px solid #88c0d0'
                          : '1px solid rgba(0, 0, 0, 0.1)',
                        '&:hover': { bgcolor: isDarkMode ? 'rgba(136, 192, 208, 0.1)' : 'grey.100' },
                        boxShadow: 1,
                      }}
                    >
                      {selectedCompanies.includes(company._id) ? (
                        <CheckBoxIcon sx={{ color: '#88c0d0' }} />
                      ) : (
                        <CheckBoxOutlineBlankIcon sx={{ color: 'text.secondary' }} />
                      )}
                    </IconButton>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <CompanyFormDialog
        open={openDialog}
        company={selectedCompany}
        onClose={() => { setOpenDialog(false); setSelectedCompany(null); }}
        onSaved={fetchCompanies}
        isAdmin={isAdmin}
      />

      {/* Detail Dialog */}
      <Dialog open={openDetailDialog} onClose={() => setOpenDetailDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight={700}>{viewCompany?.name}</Typography>
            <Chip
              label={viewCompany?.status}
              size="small"
              sx={{
                backgroundColor: viewCompany?.color,
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewCompany && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Основная информация
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                {viewCompany.address && (
                  <Box mb={1.5}>
                    <Typography variant="caption" color="text.secondary">Адрес:</Typography>
                    <Typography variant="body1">{viewCompany.address}</Typography>
                  </Box>
                )}

                {viewCompany.district && (
                  <Box mb={1.5}>
                    <Typography variant="caption" color="text.secondary">Район:</Typography>
                    <Typography variant="body1">{viewCompany.district}</Typography>
                  </Box>
                )}
                
                {viewCompany.phone && (
                  <Box mb={1.5}>
                    <Typography variant="caption" color="text.secondary">Телефон:</Typography>
                    <Typography variant="body1">{viewCompany.phone}</Typography>
                  </Box>
                )}
                
                {viewCompany.contactPerson && (
                  <Box mb={1.5}>
                    <Typography variant="caption" color="text.secondary">Контактное лицо:</Typography>
                    <Typography variant="body1">{viewCompany.contactPerson}</Typography>
                  </Box>
                )}
                
                {viewCompany.email && (
                  <Box mb={1.5}>
                    <Typography variant="caption" color="text.secondary">Email:</Typography>
                    <Typography variant="body1">{viewCompany.email}</Typography>
                  </Box>
                )}
              </Grid>

              {viewCompany.requisites && Object.values(viewCompany.requisites).some(val => val) && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Карточка предприятия (Реквизиты)
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    {viewCompany.requisites.inn && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">ИНН:</Typography>
                        <Typography variant="body2">{viewCompany.requisites.inn}</Typography>
                      </Grid>
                    )}
                    {viewCompany.requisites.kpp && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">КПП:</Typography>
                        <Typography variant="body2">{viewCompany.requisites.kpp}</Typography>
                      </Grid>
                    )}
                    {viewCompany.requisites.ogrn && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">ОГРН:</Typography>
                        <Typography variant="body2">{viewCompany.requisites.ogrn}</Typography>
                      </Grid>
                    )}
                    {viewCompany.requisites.bankName && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Банк:</Typography>
                        <Typography variant="body2">{viewCompany.requisites.bankName}</Typography>
                      </Grid>
                    )}
                    {viewCompany.requisites.bik && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">БИК:</Typography>
                        <Typography variant="body2">{viewCompany.requisites.bik}</Typography>
                      </Grid>
                    )}
                    {viewCompany.requisites.checkingAccount && (
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Расчетный счет:</Typography>
                        <Typography variant="body2">{viewCompany.requisites.checkingAccount}</Typography>
                      </Grid>
                    )}
                    {viewCompany.requisites.correspondentAccount && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Корреспондентский счет:</Typography>
                        <Typography variant="body2">{viewCompany.requisites.correspondentAccount}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              {viewCompany.additionalInfo && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Дополнительная информация
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {viewCompany.additionalInfo}
                  </Typography>
                </Grid>
              )}

              {viewCompany.assignedTo && viewCompany.assignedTo.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Назначенные менеджеры
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {viewCompany.assignedTo.map((manager) => (
                      <Chip
                        key={manager._id}
                        label={`${manager.firstName} ${manager.lastName}`}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Статус подтверждения
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {viewCompany.approved ? (
                  <Box>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Подтверждена"
                      sx={{ 
                        mb: 1, 
                        bgcolor: 'rgba(163, 190, 140, 0.2)', 
                        color: '#a3be8c',
                        border: '1px solid rgba(163, 190, 140, 0.3)',
                      }}
                    />
                    {viewCompany.approvedBy && (
                      <Typography variant="body2" color="text.secondary">
                        Подтверждена: {viewCompany.approvedBy.firstName} {viewCompany.approvedBy.lastName}
                        {viewCompany.approvedAt && ` (${new Date(viewCompany.approvedAt).toLocaleDateString('ru-RU')})`}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Chip
                    icon={<HourglassEmptyIcon />}
                    label="Ожидает подтверждения администратором"
                    sx={{ 
                      bgcolor: 'rgba(235, 203, 139, 0.2)', 
                      color: '#ebcb8b',
                      border: '1px solid rgba(235, 203, 139, 0.3)',
                    }}
                  />
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Создана: {viewCompany.createdBy?.firstName} {viewCompany.createdBy?.lastName}
                  {viewCompany.createdAt && ` (${new Date(viewCompany.createdAt).toLocaleDateString('ru-RU')})`}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button 
            variant="contained"
            startIcon={<HistoryIcon />}
            onClick={handleOpenHistory}
            disabled={!viewCompany}
            sx={{
              background: 'linear-gradient(135deg, #b48ead 0%, #5e81ac 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5e81ac 0%, #b48ead 100%)',
              }
            }}
          >
            История
          </Button>
          <Button 
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={!viewCompany}
          >
            Печать
          </Button>
          <Button onClick={() => setOpenDetailDialog(false)} variant="outlined">Закрыть</Button>
          {isAdmin && viewCompany && !viewCompany.approved && (
            <>
              <Button 
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={async () => {
                  if (window.confirm('Отклонить и удалить эту компанию?')) {
                    try {
                      await axios.post(`/api/companies/${viewCompany._id}/reject`);
                      setOpenDetailDialog(false);
                      fetchCompanies();
                    } catch (error) {
                      console.error('Ошибка отклонения:', error);
                      alert('Ошибка отклонения компании');
                    }
                  }
                }}
              >
                Отклонить
              </Button>
              <Button 
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={async () => {
                  try {
                    await axios.post(`/api/companies/${viewCompany._id}/approve`);
                    setOpenDetailDialog(false);
                    fetchCompanies();
                  } catch (error) {
                    console.error('Ошибка подтверждения:', error);
                    alert('Ошибка подтверждения компании');
                  }
                }}
                sx={{
                  background: 'linear-gradient(135deg, #a3be8c 0%, #88c0d0 100%)',
                }}
              >
                Подтвердить
              </Button>
            </>
          )}
          {isAdmin && viewCompany && (
            <Button 
              variant="contained" 
              startIcon={<EditIcon />}
              onClick={() => {
                setOpenDetailDialog(false);
                handleOpenDialog(viewCompany);
              }}
            >
              Редактировать
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Mass Assign Dialog */}
      {isAdmin && (
        <Dialog open={openMassAssignDialog} onClose={() => setOpenMassAssignDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Массовое назначение компаний</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 2 }}>
              Выбрано компаний: {selectedCompanies.length}
            </Typography>
            <TextField
              fullWidth
              select
              label="Выберите менеджера"
              value={selectedManagerForMass}
              onChange={(e) => setSelectedManagerForMass(e.target.value)}
              margin="normal"
            >
              <MenuItem value="none">
                <em>Не назначено (снять назначение)</em>
              </MenuItem>
              {managers.map((manager) => (
                <MenuItem key={manager._id} value={manager._id}>
                  {manager.firstName} {manager.lastName}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenMassAssignDialog(false)} variant="outlined">Отмена</Button>
            <Button 
              onClick={async () => {
                try {
                  const managerIds = selectedManagerForMass === 'none' ? [] : [selectedManagerForMass];
                  await Promise.all(
                    selectedCompanies.map(companyId =>
                      axios.post(`/api/companies/${companyId}/assign`, { managerIds })
                    )
                  );
                  await fetchCompanies();
                  setSelectedCompanies([]);
                  setSelectedManagerForMass('');
                  setOpenMassAssignDialog(false);
                  alert(selectedManagerForMass === 'none' 
                    ? 'Назначение снято с компаний!' 
                    : 'Компании успешно назначены!');
                } catch (error) {
                  console.error('Ошибка массового назначения:', error);
                  alert(error.response?.data?.message || 'Ошибка назначения');
                }
              }} 
              variant="contained"
              disabled={selectedManagerForMass === ''}
            >
              {selectedManagerForMass === 'none' ? 'Снять назначение' : 'Назначить'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Assign Dialog */}
      {isAdmin && (
        <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Назначить менеджеров</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              select
              label="Выберите менеджеров"
              SelectProps={{
                multiple: true,
                value: selectedCompany?.assignedTo?.map(m => m._id) || [],
                onChange: (e) => handleAssign(e.target.value),
              }}
              margin="normal"
            >
              {managers.map((manager) => (
                <MenuItem key={manager._id} value={manager._id}>
                  {manager.firstName} {manager.lastName}
                </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenAssignDialog(false)} variant="outlined">Закрыть</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* History Dialog */}
      <Dialog 
        open={historyOpen} 
        onClose={() => setHistoryOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            background: isDarkMode ? 'rgba(13, 17, 23, 0.98)' : '#ffffff',
            backdropFilter: 'blur(20px)',
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: isDarkMode 
            ? 'linear-gradient(135deg, rgba(180, 142, 173, 0.15) 0%, rgba(94, 129, 172, 0.15) 100%)'
            : 'linear-gradient(135deg, #b48ead 0%, #5e81ac 100%)',
          color: isDarkMode ? '#e6edf3' : '#ffffff',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <HistoryIcon />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              История работы с компанией: {viewCompany?.name}
            </Typography>
          </Box>
          <IconButton onClick={() => setHistoryOpen(false)} sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
              <CircularProgress />
            </Box>
          ) : historyData ? (
            <Box>
              {/* Статистика */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    background: isDarkMode ? 'rgba(136, 192, 208, 0.1)' : 'rgba(136, 192, 208, 0.08)',
                    border: '1px solid rgba(136, 192, 208, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#88c0d0' }}>
                      {historyData.stats?.totalCards || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Всего карточек
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    background: isDarkMode ? 'rgba(163, 190, 140, 0.1)' : 'rgba(163, 190, 140, 0.08)',
                    border: '1px solid rgba(163, 190, 140, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#a3be8c' }}>
                      {formatCurrency(historyData.stats?.totalDealAmount || 0)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Сумма сделок
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    background: isDarkMode ? 'rgba(180, 142, 173, 0.1)' : 'rgba(180, 142, 173, 0.08)',
                    border: '1px solid rgba(180, 142, 173, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#b48ead' }}>
                      {historyData.stats?.uniqueManagers || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Менеджеров работало
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ 
                    p: 2, 
                    borderRadius: 2, 
                    background: isDarkMode ? 'rgba(235, 203, 139, 0.1)' : 'rgba(235, 203, 139, 0.08)',
                    border: '1px solid rgba(235, 203, 139, 0.3)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#ebcb8b' }}>
                      {historyData.stats?.completedTasks || 0}/{historyData.stats?.totalTasks || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Выполнено задач
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Период работы */}
              {historyData.stats?.firstInteraction && (
                <Box sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 2, 
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2
                }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Первое взаимодействие</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatDateTime(historyData.stats.firstInteraction)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Последнее обновление</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatDateTime(historyData.stats.lastInteraction)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* История карточек */}
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BoardIcon sx={{ color: '#88c0d0' }} />
                Хронология работы
              </Typography>

              {historyData.history && historyData.history.length > 0 ? (
                historyData.history.map((item) => (
                  <Accordion 
                    key={item._id}
                    sx={{
                      mb: 1,
                      background: isDarkMode ? 'rgba(22, 27, 34, 0.6)' : '#f8fafc',
                      border: '1px solid',
                      borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
                      borderLeft: `4px solid ${item.archived ? '#8b949e' : (priorityColors[item.priority] || '#88c0d0')}`,
                      '&:before': { display: 'none' },
                      borderRadius: '8px !important',
                      overflow: 'hidden',
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, flexWrap: 'wrap' }}>
                        {/* Дата */}
                        <Box sx={{ minWidth: 100 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(item.date)}
                          </Typography>
                        </Box>
                        
                        {/* Менеджер */}
                        <Chip
                          avatar={
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem' }}>
                              {item.manager?.firstName?.[0]}{item.manager?.lastName?.[0]}
                            </Avatar>
                          }
                          label={`${item.manager?.firstName || ''} ${item.manager?.lastName || ''}`.trim() || 'Неизвестный'}
                          size="small"
                          sx={{ 
                            background: 'rgba(136, 192, 208, 0.15)',
                            border: '1px solid rgba(136, 192, 208, 0.3)',
                          }}
                        />
                        
                        {/* Доска и колонка */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={item.board?.name || 'Неизвестная доска'}
                            size="small"
                            sx={{ 
                              background: 'rgba(94, 129, 172, 0.15)',
                              border: '1px solid rgba(94, 129, 172, 0.3)',
                            }}
                          />
                          {item.column && (
                            <Chip
                              label={item.column.title}
                              size="small"
                              sx={{ 
                                background: `${item.column.color}30`,
                                border: `1px solid ${item.column.color}`,
                                color: item.column.color,
                              }}
                            />
                          )}
                        </Box>

                        {/* Сумма сделки */}
                        {item.dealAmount > 0 && (
                          <Chip
                            icon={<MoneyIcon sx={{ fontSize: 16 }} />}
                            label={formatCurrency(item.dealAmount)}
                            size="small"
                            sx={{ 
                              background: 'rgba(163, 190, 140, 0.15)',
                              border: '1px solid rgba(163, 190, 140, 0.3)',
                              color: '#a3be8c',
                              fontWeight: 700,
                            }}
                          />
                        )}

                        {/* Архивная метка */}
                        {item.archived && (
                          <Chip
                            icon={<ArchiveIcon sx={{ fontSize: 14 }} />}
                            label="В архиве"
                            size="small"
                            sx={{ 
                              background: 'rgba(139, 148, 158, 0.15)',
                              border: '1px solid rgba(139, 148, 158, 0.3)',
                              color: '#8b949e',
                            }}
                          />
                        )}

                        {/* Индикаторы */}
                        <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                          {item.tasks?.length > 0 && (
                            <Tooltip title={`${item.tasks.filter(t => t.completed).length}/${item.tasks.length} задач`}>
                              <Chip
                                icon={<TaskIcon sx={{ fontSize: 14 }} />}
                                label={`${item.tasks.filter(t => t.completed).length}/${item.tasks.length}`}
                                size="small"
                                sx={{ height: 24, '& .MuiChip-label': { px: 1 } }}
                              />
                            </Tooltip>
                          )}
                          {item.stickers?.length > 0 && (
                            <Tooltip title={`${item.stickers.length} стикеров`}>
                              <Chip
                                icon={<StickerIcon sx={{ fontSize: 14 }} />}
                                label={item.stickers.length}
                                size="small"
                                sx={{ height: 24, '& .MuiChip-label': { px: 1 } }}
                              />
                            </Tooltip>
                          )}
                          {item.notes?.length > 0 && (
                            <Tooltip title={`${item.notes.length} заметок`}>
                              <Chip
                                icon={<NotesIcon sx={{ fontSize: 14 }} />}
                                label={item.notes.length}
                                size="small"
                                sx={{ height: 24, '& .MuiChip-label': { px: 1 } }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Grid container spacing={2}>
                        {/* Район */}
                        {item.district && (
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary">Район</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              📍 {item.district}
                            </Typography>
                          </Grid>
                        )}
                        
                        {/* Описание */}
                        {item.description && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Описание</Typography>
                            <Typography variant="body2">{item.description}</Typography>
                          </Grid>
                        )}

                        {/* Задачи */}
                        {item.tasks && item.tasks.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TaskIcon sx={{ fontSize: 18, color: '#a3be8c' }} />
                              Задачи
                            </Typography>
                            <Box sx={{ pl: 1 }}>
                              {item.tasks.map((task, idx) => (
                                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                  <Checkbox 
                                    checked={task.completed} 
                                    disabled 
                                    size="small"
                                    sx={{ p: 0 }}
                                  />
                                  <Typography 
                                    variant="body2"
                                    sx={{ 
                                      textDecoration: task.completed ? 'line-through' : 'none',
                                      color: task.completed ? 'text.secondary' : 'text.primary'
                                    }}
                                  >
                                    {task.text}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          </Grid>
                        )}

                        {/* Стикеры */}
                        {item.stickers && item.stickers.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <StickerIcon sx={{ fontSize: 18, color: '#ebcb8b' }} />
                              Стикеры
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {item.stickers.map((sticker, idx) => (
                                <Chip
                                  key={idx}
                                  label={sticker.text}
                                  size="small"
                                  sx={{
                                    background: `${sticker.color}30`,
                                    border: `1px solid ${sticker.color}`,
                                    color: sticker.color,
                                    fontWeight: 600,
                                  }}
                                />
                              ))}
                            </Box>
                          </Grid>
                        )}

                        {/* Заметки */}
                        {item.notes && item.notes.length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <NotesIcon sx={{ fontSize: 18, color: '#d08770' }} />
                              Заметки
                            </Typography>
                            {item.notes.map((note, idx) => (
                              <Box 
                                key={idx}
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  background: 'rgba(235, 203, 139, 0.1)',
                                  borderRadius: 1,
                                  borderLeft: '3px solid #ebcb8b',
                                }}
                              >
                                <Typography variant="body2">{note.text}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDateTime(note.createdAt)}
                                </Typography>
                              </Box>
                            ))}
                          </Grid>
                        )}

                        {/* Мета-информация */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                              Создано: {formatDateTime(item.createdAt)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Обновлено: {formatDateTime(item.updatedAt)}
                            </Typography>
                            {item.createdBy && (
                              <Typography variant="caption" color="text.secondary">
                                Автор: {item.createdBy.firstName} {item.createdBy.lastName}
                              </Typography>
                            )}
                          </Box>
                        </Grid>

                        {/* Кнопка перехода в доску */}
                        {item.board?.name && (
                          <Grid item xs={12}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<OpenIcon />}
                              onClick={() => {
                                setHistoryOpen(false);
                                setOpenDetailDialog(false);
                                navigate(`/kanban/${encodeURIComponent(item.board.name)}`);
                              }}
                            >
                              Открыть доску "{item.board.name}"
                            </Button>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  background: isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                  borderRadius: 2,
                }}>
                  <Typography variant="body1" color="text.secondary">
                    📭 История работы с компанией пуста
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Добавьте карточку с этой компанией в любую доску
                  </Typography>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Companies;
